'use client'

import { motion } from 'framer-motion'
import SmartImage from './ui/SmartImage'
import { siteImages } from '@/data/siteImages'

interface SolutionHeroImageProps {
  /** Route folder name, e.g. 'hospital-management' */
  slug: string
  alt: string
  /** Tailwind gradient for the tint overlay, e.g. 'from-red-500 to-pink-600' */
  accent?: string
}

/**
 * A framed, animated hero image band for Solutions detail pages.
 * Resolves its photo from siteImages.solutions[slug] with a safe fallback.
 */
export default function SolutionHeroImage({ slug, alt, accent = 'from-sky-500 to-blue-600' }: SolutionHeroImageProps) {
  const src = siteImages.solutions[slug] || siteImages.hero.secondary
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.2 }}
      className="max-w-4xl mx-auto mt-12"
    >
      <div className="relative h-64 sm:h-80 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5">
        <SmartImage
          src={src}
          alt={alt}
          fill
          priority
          sizes="(max-width: 896px) 100vw, 896px"
          className="object-cover"
        />
        <div className={`absolute inset-0 bg-gradient-to-tr ${accent} opacity-25 mix-blend-multiply`} />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
      </div>
    </motion.div>
  )
}
